/**
 * The site-photo engine. 30-09-2026, migration 0185.
 *
 * Everything happens on the user's own device, before anything is uploaded:
 *
 *   1. READ     the photo the right way up (the camera's rotation tag honoured)
 *   2. FOCUS    find where the picture actually is. The photo is shrunk to a
 *               thumbnail and every pixel scored for detail (edges - the
 *               outline of a structure, a crane, people - plus colour
 *               saturation, which sky and bare ground lack). A 16:9 window is
 *               slid across it and the window holding the most detail wins,
 *               with a gentle pull towards the centre so an evenly busy photo
 *               is still framed the way a person would frame it. The user can
 *               then drag the frame if the machine chose badly.
 *   3. FRAME    crop to exactly 16:9 around that point and scale to at most
 *               1280 x 720, in halving steps so fine detail does not shimmer.
 *               1280 is what a one-third-width card needs on a sharp laptop
 *               screen - more would be bytes nobody sees.
 *   4. SQUEEZE  encode as WebP where the browser can (JPEG otherwise), and
 *               search the quality for the smallest file inside a budget,
 *               never below a floor that keeps the picture crisp.
 *
 * The result is typically 60-150 KB, from a 3-8 MB phone photo - which is
 * what keeps the dashboard fast on a site's mobile signal.
 */

export const OUT_W = 1280
export const OUT_H = 720
export const MAX_BYTES = 2 * 1024 * 1024
const BUDGET = 160 * 1024          // aim for this or less
const Q_TOP = 0.82                 // visually lossless for photographs
const Q_FLOOR = { 'image/webp': 0.6, 'image/jpeg': 0.62 } as const

export type Mime = 'image/webp' | 'image/jpeg'

export interface Source {
  image: CanvasImageSource
  w: number
  h: number
  /** Which way the 16:9 window can move, and by how many source pixels. */
  axis: 'x' | 'y' | 'none'
  cropW: number
  cropH: number
  travel: number
  /** Where the engine placed the window, 0 = left/top edge, 1 = right/bottom. */
  focal: number
  /** A small copy of the whole photo for the framing preview. */
  previewUrl: string
  originalBytes: number
}

export interface Prepared {
  blob: Blob
  mime: Mime
  width: number
  height: number
  bytes: number
  quality: number
  url: string
}

// ---------------------------------------------------------------------------
async function decode(file: File): Promise<{ image: CanvasImageSource; w: number; h: number }> {
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions)
    return { image: bmp, w: bmp.width, h: bmp.height }
  } catch {
    const url = URL.createObjectURL(file)
    try {
      const img = await new Promise<HTMLImageElement>((ok, bad) => {
        const i = new Image()
        i.onload = () => ok(i)
        i.onerror = () => bad(new Error('That file could not be read as a photo.'))
        i.src = url
      })
      return { image: img, w: img.naturalWidth, h: img.naturalHeight }
    } finally { URL.revokeObjectURL(url) }
  }
}

function canvas(w: number, h: number) {
  const c = document.createElement('canvas')
  c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h))
  return c
}

/** Step 2: where is the picture? Returns 0..1 along the free axis. */
function findFocus(image: CanvasImageSource, w: number, h: number, axis: 'x' | 'y', cropW: number, cropH: number): number {
  const scale = Math.min(1, 240 / Math.max(w, h))
  const sw = Math.max(8, Math.round(w * scale)), sh = Math.max(8, Math.round(h * scale))
  const c = canvas(sw, sh)
  const ctx = c.getContext('2d', { willReadFrequently: true })!
  ctx.drawImage(image, 0, 0, sw, sh)
  const px = ctx.getImageData(0, 0, sw, sh).data

  const lum = new Float32Array(sw * sh)
  const sat = new Float32Array(sw * sh)
  for (let i = 0, p = 0; i < lum.length; i++, p += 4) {
    const r = px[p], g = px[p + 1], b = px[p + 2]
    lum[i] = 0.299 * r + 0.587 * g + 0.114 * b
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b)
    sat[i] = mx === 0 ? 0 : (mx - mn) / mx
  }

  // Detail along the free axis: a column (or row) sum of edge strength and colour.
  const len = axis === 'x' ? sw : sh
  const line = new Float64Array(len)
  for (let y = 1; y < sh - 1; y++) {
    for (let x = 1; x < sw - 1; x++) {
      const i = y * sw + x
      const e = Math.abs(lum[i + 1] - lum[i - 1]) + Math.abs(lum[i + sw] - lum[i - sw]) + 60 * sat[i]
      line[axis === 'x' ? x : y] += e
    }
  }

  const win = Math.round((axis === 'x' ? cropW : cropH) * scale)
  const travel = len - win
  if (travel <= 0) return 0.5
  let sum = 0
  for (let i = 0; i < win; i++) sum += line[i]
  const total = line.reduce((a, v) => a + v, 0) || 1
  let best = -Infinity, bestAt = travel / 2
  for (let at = 0; at <= travel; at++) {
    if (at > 0) sum += line[at + win - 1] - line[at - 1]
    // A gentle pull to the centre: a person frames the middle unless the
    // picture is plainly elsewhere.
    const offCentre = Math.abs(at - travel / 2) / (travel / 2 || 1)
    const score = sum / total - 0.12 * offCentre
    if (score > best) { best = score; bestAt = at }
  }
  return bestAt / travel
}

/** Steps 1 and 2. */
export async function loadSource(file: File): Promise<Source> {
  if (!/^image\//.test(file.type)) throw new Error('Choose a photograph - a JPEG, PNG or WebP image.')
  const { image, w, h } = await decode(file)
  const ratio = w / h, target = 16 / 9
  let axis: Source['axis'] = 'none', cropW = w, cropH = h
  if (ratio > target * 1.005) { axis = 'x'; cropW = Math.round(h * target) }
  else if (ratio < target / 1.005) { axis = 'y'; cropH = Math.round(w / target) }
  const travel = axis === 'x' ? w - cropW : axis === 'y' ? h - cropH : 0
  const focal = axis === 'none' ? 0.5 : findFocus(image, w, h, axis, cropW, cropH)

  // A light preview of the whole photo for the framing box.
  const ps = Math.min(1, 900 / Math.max(w, h))
  const pc = canvas(w * ps, h * ps)
  pc.getContext('2d')!.drawImage(image, 0, 0, pc.width, pc.height)
  const previewBlob = await new Promise<Blob | null>((ok) => pc.toBlob(ok, 'image/jpeg', 0.8))
  const previewUrl = previewBlob ? URL.createObjectURL(previewBlob) : ''

  return { image, w, h, axis, cropW, cropH, travel, focal, previewUrl, originalBytes: file.size }
}

/** Step 3: crop at `at` (0..1 along the free axis) and scale in halving steps. */
function frame(src: Source, at: number): HTMLCanvasElement {
  const off = Math.round(Math.min(1, Math.max(0, at)) * src.travel)
  const sx = src.axis === 'x' ? off : 0
  const sy = src.axis === 'y' ? off : 0
  const outW = Math.min(OUT_W, src.cropW)
  const outH = Math.round(outW * 9 / 16)

  let cur = canvas(src.cropW, src.cropH)
  let ctx = cur.getContext('2d')!
  ctx.drawImage(src.image, sx, sy, src.cropW, src.cropH, 0, 0, src.cropW, src.cropH)
  // Halve until within 2x of the target: each step averages cleanly.
  while (cur.width / 2 >= outW) {
    const next = canvas(cur.width / 2, cur.height / 2)
    const nctx = next.getContext('2d')!
    nctx.imageSmoothingQuality = 'high'
    nctx.drawImage(cur, 0, 0, next.width, next.height)
    cur = next
  }
  const out = canvas(outW, outH)
  ctx = out.getContext('2d')!
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(cur, 0, 0, outW, outH)
  return out
}

const toBlob = (c: HTMLCanvasElement, mime: Mime, q: number) =>
  new Promise<Blob | null>((ok) => c.toBlob(ok, mime, q))

let webpOk: boolean | null = null
async function canWriteWebp(): Promise<boolean> {
  if (webpOk !== null) return webpOk
  const b = await toBlob(canvas(2, 2), 'image/webp', 0.8)
  webpOk = !!b && b.type === 'image/webp'
  return webpOk
}

/** Steps 3 and 4: the file that will be uploaded. */
export async function prepare(src: Source, at: number): Promise<Prepared> {
  const c = frame(src, at)
  const mime: Mime = (await canWriteWebp()) ? 'image/webp' : 'image/jpeg'
  const floor = Q_FLOOR[mime]

  let q = Q_TOP
  let blob = await toBlob(c, mime, q)
  if (blob && blob.size > BUDGET) {
    // The highest quality that fits the budget, never below the floor.
    let lo: number = floor, hi: number = Q_TOP, fit: Blob | null = null, fitQ: number = floor
    for (let i = 0; i < 5; i++) {
      const mid = (lo + hi) / 2
      const b = await toBlob(c, mime, mid)
      if (b && b.size <= BUDGET) { fit = b; fitQ = mid; lo = mid } else { hi = mid }
    }
    if (fit) { blob = fit; q = fitQ }
    else { blob = await toBlob(c, mime, floor); q = floor }
  }
  if (!blob) throw new Error('This browser could not prepare the photo.')
  if (blob.size > MAX_BYTES) throw new Error('This photo is still over 2 MB after preparing it. Try another one.')
  return {
    blob, mime, width: c.width, height: c.height, bytes: blob.size,
    quality: Math.round(q * 100) / 100, url: URL.createObjectURL(blob),
  }
}

export const kb = (n: number) => n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`
